import { css } from "@emotion/react";

import { colors } from "../../colors";
import type { RadioSize, RadioStyleProps } from "./types";

const defaultStyles: Required<RadioStyleProps> = {
  checkedColor: colors.primary[300], // #FF4949
  borderColor: colors.gray[200], // #cfd8e2
  focusRingColor: colors.primary[100], // #ffdbdb
  labelColor: colors.gray[800], // #2F3D4E
  descriptionColor: colors.gray[500], // #7089a9
  disabledOpacity: 0.5,
  size: "medium" as RadioSize,
  borderRadius: "50%",
  spacing: "8px",
  labelFontWeight: "500",
  labelFontSize: "14px",
  descriptionFontSize: "12px",
};

const sizeMap = {
  small: { size: "16px", dotSize: "6px" },
  medium: { size: "20px", dotSize: "8px" },
  large: { size: "24px", dotSize: "10px" },
};

export const getRadioStyles = (
  size: "small" | "medium" | "large" = "medium",
  checked: boolean = false,
  disabled: boolean = false,
  styleProps?: Partial<RadioStyleProps>,
) => {
  const merged = { ...defaultStyles, ...styleProps };
  const sizeConfig = sizeMap[size];

  const checkedColor = styleProps?.checkedColor || merged.checkedColor;
  const borderColor = styleProps?.borderColor || merged.borderColor;
  const focusColor = styleProps?.focusRingColor || merged.focusRingColor;
  const hoverBorderColor = styleProps?.checkedColor || colors.primary[400];

  return css`
    position: relative;
    width: ${sizeConfig.size};
    height: ${sizeConfig.size};
    border: 1px solid ${checked ? checkedColor : borderColor};
    border-radius: ${merged.borderRadius};
    background: ${checked ? checkedColor : "white"};
    cursor: ${disabled ? "not-allowed" : "pointer"};
    opacity: ${disabled ? merged.disabledOpacity : 1};
    transition: all 0.2s ease-in-out;

    /* Focus ring */
    &:focus-within {
      outline: none;
      box-shadow: 0 0 0 3px ${focusColor}40;
    }

    /* Checked dot */
    ${checked &&
    css`
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${sizeConfig.dotSize};
        height: ${sizeConfig.dotSize};
        border-radius: 50%;
        background: white;
      }
    `}

    /* Hover effect */
    &:hover:not(:disabled) {
      border-color: ${checked ? checkedColor : hoverBorderColor};
    }
  `;
};
